import type { ReactElement } from 'react';
import { Modal, type ModalAction } from './Modal';

export interface UpdateConfirmDialogProps {
  open: boolean;
  /** Number of dirty docs shown in the prompt copy. */
  dirtyCount: number;
  /** "Save all & update": save every dirty doc then proceed to install. */
  onConfirm: () => void;
  /** Esc / backdrop / Cancel: keep everything open, abort the install. */
  onCancel: () => void;
}

/**
 * Save-all gate shown before an update install. Mirrors the
 * `CloseConfirmDialog` "window" variant in copy and structure but with
 * update-specific labels. By explicit design choice there is NO
 * "Update without saving" path: the user cannot lose unsaved work to
 * an update; if Save As is cancelled, the install aborts (see spec §3.3).
 */
export function UpdateConfirmDialog({
  open,
  dirtyCount,
  onConfirm,
  onCancel,
}: UpdateConfirmDialogProps): ReactElement | null {
  if (!open) return null;
  const plural = dirtyCount !== 1;
  // Non-destructive: "Save all & update" is the affirmative Enter default.
  const actions: ModalAction[] = [
    { id: 'cancel', label: 'Cancel', onSelect: onCancel, accelerator: 'c', isCancel: true },
    { id: 'confirm', label: 'Save all & update', onSelect: onConfirm, accelerator: 's', variant: 'primary', isDefault: true },
  ];
  return (
    <Modal open={open} onClose={onCancel} title="Unsaved changes" actions={actions}>
      <p className="mp-modal__text">
        You have unsaved changes in{' '}
        <strong>
          {dirtyCount} document{plural ? 's' : ''}
        </strong>
        . Save them before updating?
      </p>
    </Modal>
  );
}
